/**
 * Breadcrumbs.tsx - Standalone Breadcrumbs Component
 * 
 * Can be used independently or as part of NavigationBar
 */

import React from 'react';
import { useNavigatr } from '../useNavigatr';
import './Breadcrumbs.css';

export interface BreadcrumbItem {
  label: string;
  route: string;
}

export interface BreadcrumbsProps {
  /** App name for Navigatr instance */
  appName: string;
  
  /** Custom breadcrumbs (overrides Navigatr state) */
  items?: BreadcrumbItem[];
  
  /** Callback when breadcrumb is clicked */
  onNavigate?: (route: string) => void;
  
  /** Custom labels for breadcrumbs */
  labels?: Record<string, string>;
  
  /** Custom separator */
  separator?: string;
  
  /** Custom class name */
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  appName,
  items,
  onNavigate,
  labels = {},
  separator = '/',
  className = '',
}) => {
  const { navigatr } = useNavigatr(appName) as any;
  const breadcrumbs = items || (navigatr ? navigatr.getBreadcrumbs() : []);

  if (breadcrumbs.length === 0) {
    return null;
  }

  const handleClick = (route: string, index: number) => {
    // Don't navigate if it's the last (current) item
    if (index === breadcrumbs.length - 1) return;
    
    if (onNavigate) {
      onNavigate(route);
    }
  };

  return (
    <nav className={`breadcrumbs ${className}`} aria-label="Breadcrumb">
      <ol className="breadcrumbs-list">
        {breadcrumbs.map((crumb: BreadcrumbItem, index: number) => (
          <li key={index} className="breadcrumbs-item">
            {index > 0 && (
              <span className="breadcrumbs-separator" aria-hidden="true">
                {separator}
              </span>
            )}
            <button
              className={`breadcrumbs-link ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
              onClick={() => handleClick(crumb.route, index)}
              disabled={index === breadcrumbs.length - 1}
              aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}
            >
              {labels[crumb.label] || crumb.label}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
};
